import { ComplianceDocument } from '../compliance/compliance.document';
import { Entity, TimestampDetails } from '../entity';
import { ActivitySharingSettings } from '../settings/general.settings';
import { ActivityTag } from '../shared/activity';
import { Address } from '../shared/address';
import { PhotoSettings } from '../photo.settings/photo.settings';
import { DeviceType } from '../shared/user.device.info';
import { Label } from '../shared/label';
import { NamedPhoneNumber } from '../shared/named.phone.number';
import { Bank } from '../shared/bank';
import { Certificate } from '../shared/certificate';
import { TimesheetEntry } from '../time.billing/timesheet.entry';
import { Company } from './company';
import { Category } from './category';
import { SecurityRoleType, SecurityRoleTiny, SecurityRole } from '../../security/security.role';
import { AccessType } from '../contact/contact.options';
import { UserType } from './user.type';


export enum UserInvitationStatus {
    Invited = 'Invited',
    Confirmed = 'Confirmed',
    Expired = 'Expired',
    System = 'System',
    Connected = 'Connected'
}

export enum AuthorisationProvider {
    WorkBuddy = 'WorkBuddy',
    Email = 'Email',
    Google = 'Google',
    Microsoft = 'Microsoft',
    Passkey = 'Passkey',
}

export enum MFAMethod {
    Email = 'Email',
    Authenticator = 'Authenticator',
    Passkey = 'Passkey',
    Password = 'Password'
}

export enum MFAStatus {
    NotRequired = 'NotRequired',              // User has no MFA enabled
    Required = 'Required',                    // MFA enabled but not verified this session
    Verified = 'Verified',                    // MFA verified this session
    SkippedPasskey = 'SkippedPasskey',       // Passkey login, MFA not needed
    SkippedLoginMethod = 'SkippedLoginMethod', // Email login with Email MFA - can't use same method
    SkippedMobileApp = 'SkippedMobileApp'    // Mobile app login, MFA enforcement configurable
}

export interface UserSecurity {
    administrator: boolean;
    supervisor: boolean;
    technician: boolean;
    contractor: boolean;
}

export interface UserEmailChange {
    _id?: string;
    email: string;
    date: string;
    active: boolean;
}

export interface UserInvitation {
    _id?: string;
    tenantId?: string;
    owner?: string;
    inviteDate?: Date;
    expireDate?: Date;
    status?: UserInvitationStatus;
    security?: SecurityRoleType;
    billingEmail?: string;

    firstName?: string;
    lastName?: string;
    companyName?: string;
    dispatchEmail?: string;
    enableJsonFile?: boolean;
    taxId?: string;
    expenseTaxRate?: Entity;
    billEnabled?: boolean;
    phone?: string;
    schedulingEnabled?: boolean;
    hidePrices?: boolean;

    /** Tenant URL for redirect (e.g. 'tarek.workbuddy.dev'). Populated by getInvitation query. */
    tenantUrl?: string;
    /** Tenant name for redirect. Populated by getInvitation query. */
    tenantName?: string;
}

export interface TenantTiny extends Entity {
    url: string;
    logo?: string;
    invitation?: UserInvitation;
    userName?: string;
    accessTypes?: AccessType[];
    entities?: Entity[];
}

export interface FingerPrintTiny {
    browser?: string;
    deviceType?: DeviceType;
    os?: string;
    location?: string;
    deviceId?: string;
}

export interface TokenUser extends Entity, FingerPrintTiny {
    originalUserId?: string;
    /** Used by ID handoff token validation and legacy login compatibility. */
    tokenId?: string;
    /** @deprecated Legacy login versioning field. Use sessionVersion for durable auth-session versioning. */
    tokenVersion?: number;
    sessionId?: string;
    sessionVersion?: number;
    tenantId?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    email?: string;
    userName?: string;
    phone?: string;
    mobile?: string;
    image?: string;
    locked?: boolean;
    tenants?: TenantTiny[];
    isMobile?: boolean;
    refreshToken?: string;
    systemAdmin?: boolean;
    emailOnly?: boolean;
    invite?: boolean;
    complianceExpiry?: Date;
    newComplianceAdded?: Date;
    disabled?: boolean;
    feedUserId?: string;
    mfaStatus?: MFAStatus;
    mfaMethod?: MFAMethod;
    mfaVerifiedAt?: Date;
    authorisation?: AuthorisationProvider;
}

export interface TenantUser extends TokenUser {

    tenantUrl?: string;
    tenantName?: string;
    securityRole?: Entity;
    system?: boolean;
    showWizard?: boolean;
    pricebook?: Entity;
    invitations?: UserInvitation[];
    company?: Company;
    banks?: Bank[];
    categories?: Category[];
    certificates?: Certificate[];
    createdAt?: Date;

    parent?: Entity;
    type?: UserType;
    invite?: boolean;
    hidePrices?: boolean;
    expenseTaxRate?: Entity;
    billingEmail?: string;
    dispatchEmail?: string;
    enableJsonFile?: boolean;

    firstName?: string;
    lastName?: string;
    companyName?: string;
    taxId?: string;
    billEnabled?: boolean;
    phone?: string;
    schedulingEnabled?: boolean;
    password?: string;
    tags?: ActivityTag[];
    labels?: Label[];

    mailingAddress?: Address;
    schedulingAddress?: Address;

    phoneNumbers?: NamedPhoneNumber[];

    role?: SecurityRole;
    timezone?: string;
    feedUserId?: string;

    photoSettingsForApp?: PhotoSettings;
    activitySharingSettings?: ActivitySharingSettings;
    userHash?: string;

}

export interface MultiTokenUserTenant {
    tenant?: TenantTiny;
    role?: SecurityRoleTiny;
    userName?: string;
    timezone?: string;
    /** Used by legacy login compatibility. */
    tokenId?: string;
    /** @deprecated Legacy login versioning field. Use sessionVersion for durable auth-session versioning. */
    tokenVersion?: number;
    sessionId?: string;
    sessionVersion?: number;
    tenantUrl?: string;
}


export interface MultiTokenUser extends Entity {
    isMobile?: boolean;
    tokenId: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    locked?: boolean;
    image?: string;
    tenants?: MultiTokenUserTenant[];
    mfaStatus?: MFAStatus;
    mfaMethod?: MFAMethod;
    mfaVerifiedAt?: Date;
    authorisation?: AuthorisationProvider;
}

export interface Passkey {
    _id: string;
    userId: string;
    publicKey: Uint8Array;
    webAuthnUserId: string;
    counter: number;
    backupStatus: boolean;
    deviceType: string;
    transports: any[];
    lastUsed?: Date;
    createdAt?: Date;
}

export interface OTP extends TimestampDetails {
    _id: string;
    secret: string;
    expiresAt: Date;
}

export interface Session<T = any> {
    _id: string;
    data: T;
    expiresAt: Date;
}

export interface SocialLoginId {
    _id: string;
    verified: boolean;
}

export interface AuthenticationUser extends Entity {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    password?: string;
    newPassword?: string;
    emailChange?: UserEmailChange;
    system?: boolean;
    image?: string;
    locked?: boolean;
    lastLogin?: Date;
    invalidLogins?: number;
    tenants?: TenantTiny[];
    company?: Company;
    banks?: Bank[];
    certificates?: Certificate[];
    systemAdmin?: boolean;

    billingEmail?: string;
    dispatchEmail?: string;
    enableJsonFile?: boolean;
    taxId?: string;
    billEnabled?: boolean;
    schedulingEnabled?: boolean;
    hidePrices?: boolean;

    microsoft?: SocialLoginId;
    google?: SocialLoginId;

}

export interface ValidatedUser {
    email?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    name?: string;
    phone?: string;
    mobile?: string;
    parent?: Entity;
    currentUser?: Entity;
}

export enum SettingUserTypes {
    Employees = 'Employees',
    Subcontractors = 'Subcontractors',
    PendingInvitations = 'Invitations'
}

export enum UserActivityMode {
    Push = 'Push',
    Pull = 'Pull',
}

export interface ContactUser extends Entity {
    type?: UserType;
    email?: string;
    mobile?: string;
    address?: Address;
}

export interface TenantUserWithCompliance extends TenantUser {
    compliance: ComplianceDocument[];
}

export interface TenantUserWithTimesheets extends TenantUser {
    timesheets: TimesheetEntry[];
}
